import { DraftChip } from 'components/drafts/DraftChip';
import { SvgIconComponent } from 'components/icons';
import { ComponentProps, createContext, MouseEvent, ReactNode, useContext } from 'react';
import { useIntl } from 'react-intl';
import { GlobalSearchEntityType } from 'types/globalSearch';
import { cn } from 'utils/cn';
import { ENTITY_TYPE_FAMILY, FAMILY } from 'utils/families';
import { ENTITY_TYPE_LABEL } from './entityDisplay';
import { HighlightedText } from './HighlightedText';

/**
 * Set by the new design's ⌘K dialog around its panel, where rows, headings and the exact-match row take
 * the palette's look. Unset — the classic header, the narrow sheet — they draw as they always have.
 */
export const PaletteContext = createContext(false);

const PALETTE_ROW =
    'text-foreground flex h-12 cursor-pointer items-center gap-3 rounded-lg px-2.5 no-underline [&_mark]:bg-transparent [&_mark]:font-semibold';

/**
 * Whether a click on a link should be handled in the app rather than by the browser.
 *
 * A search row is a real `<a>` so it can be middle-clicked, opened in a new tab and read off the
 * status bar; every one of those has to keep working, which is what this lets through.
 */
export function isPlainLeftClick(event: MouseEvent<HTMLAnchorElement>): boolean {
    return (
        !event.defaultPrevented &&
        event.button === 0 &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey &&
        !event.altKey
    );
}

export type GlobalSearchRowProps = Omit<ComponentProps<'a'>, 'children' | 'href' | 'id' | 'title'> & {
    /**
     * The option's DOM id. The search box points `aria-activedescendant` at it, so it has to be
     * unique across everything the panel draws at once.
     */
    id: string;
    icon: SvgIconComponent;
    /**
     * The record's family, which the palette names in a column of its own where the glyph would be. Pages,
     * actions and the ask row have none.
     */
    entityType?: GlobalSearchEntityType;
    /**
     * Plain text carrying the highlight sentinels, rendered through {@link HighlightedText}; an action's
     * title may come already built.
     */
    title: ReactNode;
    /** Marks the record as a draft, after its title. */
    draft?: boolean;
    /** The second line, already built by the caller — a highlighted quote, or the record's facts. */
    secondary?: ReactNode;
    /** The second line's colour when the two kinds differ in it. See `secondaryLineClass`. */
    secondaryClassName?: string;
    /** The right-hand column: how long ago a recent was opened. Results carry nothing here. */
    trailing?: ReactNode;
    href: string;
    /** Drawn as the selection; there is one across the whole panel, and only the keys move it. */
    selected?: boolean;
    /** The palette's Ask Crespect AI row, drawn in the AI colours. */
    tone?: 'ai';
    /** The pointer has come onto the row. It raises the peek card; it never moves the selection. */
    onHover?: () => void;
    /** Open the row in this tab. Called for a plain left click and for the keyboard. */
    onSelect: () => void;
};

/**
 * One result row — the single row shape every search surface draws, whatever it is listing.
 *
 * It knows next to nothing of what it shows: the caller picks the glyph, builds both lines and says
 * where the row goes; only the palette reads the family, to name it or ink its glyph. That is what
 * lets the hover peek wrap it and the narrow sheet draw it at its own width, neither of them needing
 * a row of their own.
 */
export function GlobalSearchRow({
    id,
    icon: Icon,
    entityType,
    title,
    draft = false,
    secondary,
    secondaryClassName,
    trailing,
    href,
    selected = false,
    tone,
    onHover,
    onSelect,
    className,
    onClick,
    onMouseEnter,
    ...rest
}: GlobalSearchRowProps) {
    const intl = useIntl();
    const palette = useContext(PaletteContext);
    const ai = tone === 'ai';

    const titleText = typeof title === 'string' ? <HighlightedText text={title} /> : title;
    const typeLabel = entityType && ENTITY_TYPE_LABEL[entityType];
    const family = entityType && ENTITY_TYPE_FAMILY[entityType];
    let lead: ReactNode;
    if (!palette) {
        lead = (
            <span
                className={cn(
                    'flex size-7 flex-none items-center justify-center rounded-[8px]',
                    selected ? 'bg-primary text-grey-0' : 'bg-primary-surface text-primary'
                )}
            >
                <Icon aria-hidden className="size-4" />
            </span>
        );
    } else if (typeLabel) {
        // The section heading names the family to a screen reader, and the best-bet row, outside any section, itself.
        lead = (
            <span
                aria-hidden
                className={cn(
                    'text-caption new:text-label new:tracking-[0.06em] w-16 flex-none font-semibold whitespace-nowrap uppercase',
                    typeLabel.className
                )}
            >
                {intl.formatMessage({ id: typeLabel.labelId })}
            </span>
        );
    } else {
        lead = (
            <span
                className={cn(
                    'flex w-16 flex-none',
                    ai ? 'text-ai' : family ? FAMILY[family].ink : entityType ? 'text-grey-600' : 'text-primary'
                )}
            >
                <Icon aria-hidden className="size-4" />
            </span>
        );
    }

    return (
        <a
            {...rest}
            id={id}
            href={href}
            role="option"
            aria-selected={selected}
            onMouseEnter={(event) => {
                onMouseEnter?.(event);
                onHover?.();
            }}
            onClick={(event) => {
                onClick?.(event);
                if (!isPlainLeftClick(event)) {
                    return;
                }
                event.preventDefault();
                onSelect();
            }}
            className={cn(
                palette ? PALETTE_ROW : 'flex cursor-pointer items-start gap-2.5 px-3.5 py-1.5 no-underline',
                // Below the breakpoint this row is only ever drawn in the narrow sheet, where it is
                // tapped rather than clicked and a row without a second line would come up short.
                !palette && 'max-sm:min-h-11',
                palette && !secondary && 'h-10',
                palette && ai && 'text-ai-ink h-11',
                selected && (palette ? (ai ? 'bg-ai-soft' : 'bg-primary-wash') : 'bg-primary-surface'),
                // The pointer gets a lighter wash of its own, so it is never mistaken for the selection.
                !selected && !ai && 'hover:bg-grey-50',
                palette && ai && !selected && 'bg-ai-wash',
                className
            )}
        >
            {lead}
            <span className="min-w-0 flex-1">
                <span
                    className={cn(
                        palette
                            ? 'block truncate text-sm leading-[1.3] font-medium'
                            : 'text-grey-700 text-meta block truncate leading-[1.54] font-semibold',
                        draft && 'flex items-center gap-1.5'
                    )}
                >
                    {draft ? (
                        <>
                            <span className="truncate">{titleText}</span>
                            <DraftChip className="flex-none" />
                        </>
                    ) : (
                        titleText
                    )}
                </span>
                {secondary !== undefined && secondary !== null && (
                    <span
                        className={cn(
                            'block truncate text-xs',
                            palette ? 'leading-[1.3]' : 'leading-[1.5]',
                            secondaryClassName ?? 'text-grey-500'
                        )}
                    >
                        {secondary}
                    </span>
                )}
            </span>
            {trailing !== undefined && trailing !== null && (
                <span
                    className={
                        palette
                            ? cn('flex-none text-xs', ai ? 'text-ai font-mono' : 'text-grey-600')
                            : 'text-grey-600 flex-none text-xs leading-[1.5]'
                    }
                >
                    {trailing}
                </span>
            )}
            <EnterKeyCap visible={selected} />
        </a>
    );
}

/**
 * The cap saying Enter opens this row, inked only on the marked one but boxed on every row: the
 * title beside it is a `truncate` in the only flexible column, so a cap that came and went would
 * re-cut that title on every row the pointer crosses. The palette draws a bare glyph, held the same
 * way. Hidden where the panel is reached by touch, which has no such key.
 */
export function EnterKeyCap({ visible }: { visible: boolean }) {
    const palette = useContext(PaletteContext);

    return (
        <span
            aria-hidden
            className={cn(
                palette
                    ? 'text-grey-600 flex-none font-mono text-xs'
                    : 'border-primary-subtle bg-paper text-primary text-caption flex-none self-center rounded-[4px] border px-1.5 py-0.5 font-mono leading-none max-sm:hidden',
                !visible && 'invisible'
            )}
        >
            ↵
        </span>
    );
}
